// Admin-only dialog: broadcast one message to every vendor / every
// organizer / everyone. Each recipient gets an in-app notification +
// their own thread row, so future replies flow through the same
// AdminMessageUserDialog UI.
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Loader2, Megaphone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const AUDIENCES = [
  { value: "vendor",    label: "All vendors" },
  { value: "organizer", label: "All organizers" },
  { value: "all",       label: "Everyone (vendors + organizers)" },
];

export default function AdminBroadcastDialog({ open, onOpenChange, onSent }) {
  const [audience, setAudience] = useState("vendor");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState({ vendor: 0, organizer: 0, all: 0 });

  useEffect(() => {
    if (!open) {
      // Reset composer state when closing
      setSubject("");
      setMessage("");
      setSendEmail(false);
      setBusy(false);
      return;
    }
    api
      .get("/admin/broadcast/audience-counts")
      .then(({ data }) => setCounts(data))
      .catch(() => {});
  }, [open]);

  const audienceCount = counts[audience] || 0;

  const submit = async (e) => {
    e.preventDefault();
    const s = subject.trim();
    const m = message.trim();
    if (!s || !m) {
      toast.error("Please add a subject and a message.");
      return;
    }
    if (
      !window.confirm(
        `Send "${s}" to ${audienceCount} ${audience === "all" ? "users" : audience + "s"}${
          sendEmail ? " (in-app + email)" : " (in-app only)"
        }?`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        audience,
        subject: s,
        message: m,
        send_email: sendEmail,
      });
      const parts = [`Sent to ${data.recipients} recipients (in-app)`];
      if (sendEmail) {
        parts.push(
          `email: ${data.email_ok} delivered${
            data.email_failed ? `, ${data.email_failed} failed` : ""
          }`
        );
      }
      toast.success(parts.join(" · "));
      onSent?.(data);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send broadcast. Try again in a moment."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange?.(next)}>
      <DialogContent className="sm:max-w-lg" data-testid="admin-broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Megaphone size={22} style={{ color: "var(--terracotta)" }} />
            Broadcast to everyone
          </DialogTitle>
          <DialogDescription>
            One message, whole audience. Every recipient gets it in their
            in-app inbox and can reply from there.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Audience</Label>
            <div
              className="grid grid-cols-1 gap-2"
              data-testid="admin-broadcast-audience"
            >
              {AUDIENCES.map((a) => {
                const active = audience === a.value;
                return (
                  <button
                    key={a.value}
                    type="button"
                    data-testid={`admin-broadcast-audience-${a.value}`}
                    aria-pressed={active}
                    onClick={() => setAudience(a.value)}
                    disabled={busy}
                    className="text-left border-2 rounded-lg p-3 transition"
                    style={{
                      borderColor: active
                        ? "var(--terracotta)"
                        : "var(--border-warm)",
                      backgroundColor: active
                        ? "rgba(201,67,42,0.06)"
                        : "transparent",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{a.label}</div>
                      <div
                        className="text-sm font-mono"
                        style={{
                          color: active
                            ? "var(--terracotta)"
                            : "var(--ink-soft)",
                        }}
                      >
                        {counts[a.value] ?? "…"}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="broadcast-subject">Subject</Label>
            <Input
              id="broadcast-subject"
              data-testid="admin-broadcast-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. AddVendTour v1.2 is live"
              maxLength={200}
              autoFocus
              disabled={busy}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="broadcast-body">Message</Label>
            <Textarea
              id="broadcast-body"
              data-testid="admin-broadcast-body"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"Hi everyone,\n\nWe just shipped…"}
              rows={7}
              maxLength={5000}
              disabled={busy}
            />
            <div
              className="text-[11px] text-right"
              style={{ color: "var(--ink-soft)" }}
            >
              {message.length} / 5000
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="broadcast-send-email"
              data-testid="admin-broadcast-send-email"
              checked={sendEmail}
              onCheckedChange={(v) => setSendEmail(Boolean(v))}
              disabled={busy}
            />
            <Label
              htmlFor="broadcast-send-email"
              className="text-sm font-normal cursor-pointer"
            >
              Also send by email ({audienceCount} inbox
              {audienceCount === 1 ? "" : "es"})
            </Label>
          </div>

          {sendEmail && audienceCount > 100 && (
            <div
              className="text-xs p-2.5 rounded-md flex gap-2"
              style={{
                backgroundColor: "rgba(201,162,75,0.15)",
                color: "#8a6a2b",
              }}
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              Heads up — this will send {audienceCount} emails via Resend.
              Confirm before sending to avoid deliverability issues.
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={busy}
              data-testid="admin-broadcast-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-broadcast-send"
              style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" />
                  Sending to {audienceCount}…
                </>
              ) : (
                <>
                  <Send size={14} className="mr-2" />
                  Send to {audienceCount}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
